import React, { useEffect, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaClock,
  FaUser,
  FaComment,
  FaPaperPlane,
  FaChevronRight,
  FaSpinner,
  FaCheckCircle,
  FaExclamationCircle,
} from "react-icons/fa";

const ContactSection = () => {
  const sectionRef = useRef(null);
  const cardRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    if (cardRef.current) observer.observe(cardRef.current);

    return () => observer.disconnect();
  }, []);

  const mapEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3594.994463270365!2d82.64152611498398!3d25.7066710836426!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399118d0cae4b8b3%3A0x4b9b6b6c6c0f0e6!2sJaunpur%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1647600000000!5m2!1sen!2sin";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (feedback.message) setFeedback({ type: "", message: "" });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setFeedback({ type: "error", message: "Please enter your full name." });
      return false;
    }
    if (!formData.email.trim()) {
      setFeedback({ type: "error", message: "Please enter your email address." });
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFeedback({ type: "error", message: "Please enter a valid email address." });
      return false;
    }
    if (!formData.message.trim()) {
      setFeedback({ type: "error", message: "Please enter your message." });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setFeedback({ type: "", message: "" });

    try {
      const serviceID = "YOUR_SERVICE_ID";   // e.g., service_abc123
      const templateID = "YOUR_TEMPLATE_ID"; // e.g., template_xyz789
      const publicKey = "YOUR_PUBLIC_KEY";   // e.g., user_abc123
      // ---------------------------------------------------------

      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        message: formData.message,
      };

      await emailjs.send(serviceID, templateID, templateParams, publicKey);

      setFeedback({
        type: "success",
        message: "Thank you! Your message has been sent. We'll get back to you soon.",
      });
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("EmailJS error:", error);
      setFeedback({
        type: "error",
        message: error.message || "Sorry, something went wrong. Please try again later.",
      });
    } finally {
      setLoading(false);
      if (feedback.type === "success") {
        setTimeout(() => setFeedback({ type: "", message: "" }), 3000);
      }
    }
  };

  return (
    <section className="w-full bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="w-full px-4 py-16" ref={sectionRef}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-blue-600 via-indigo-500 to-blue-600 bg-clip-text text-transparent animate-gradient">
                Contact Us
              </span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We're here to help – reach out anytime
            </p>
          </div>
          <div
            ref={cardRef}
            className="group opacity-0 transition-all duration-700 transform translate-y-8 hover:-translate-y-1 hover:shadow-xl rounded-2xl shadow-md overflow-hidden bg-gradient-to-br from-blue-50 via-white to-indigo-50 border-t-4 border-blue-500"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              <div className="p-8 md:p-10">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <FaMapMarkerAlt className="text-blue-600" />
                  Contact Details
                </h3>
                <div className="flex items-start gap-4 mb-5 group/item hover:transform hover:-translate-y-0.5 transition-all duration-300">
                  <div className="bg-blue-100 p-3 rounded-lg group-hover/item:bg-blue-200 transition-colors">
                    <FaMapMarkerAlt className="text-blue-600 text-base" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Address</h4>
                    <p className="text-gray-600 text-sm">
                      Village - Kushnow, Post - Bhaupur<br />
                      District - Jaunpur<br />
                      Pin - 222136<br />
                      <span className="text-blue-600 font-medium">Uttar Pradesh</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 mb-5 group/item hover:transform hover:-translate-y-0.5 transition-all duration-300">
                  <div className="bg-indigo-100 p-3 rounded-lg group-hover/item:bg-indigo-200 transition-colors">
                    <FaPhoneAlt className="text-indigo-600 text-base" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Call Us</h4>
                    <a href="tel:+919956808612" className="block text-gray-600 text-sm hover:text-blue-600 transition">
                      +91 99568 08612
                    </a>
                    <a href="tel:+919628362187" className="block text-gray-600 text-sm hover:text-blue-600 transition">
                      +91 96283 62187
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4 mb-5 group/item hover:transform hover:-translate-y-0.5 transition-all duration-300">
                  <div className="bg-purple-100 p-3 rounded-lg group-hover/item:bg-purple-200 transition-colors">
                    <FaEnvelope className="text-purple-600 text-base" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Email</h4>
                    <a href="mailto:rdspharmacycollege@gmail.com" className="text-gray-600 text-sm hover:text-purple-600 transition break-all">
                      rdspharmacycollege@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4 group/item hover:transform hover:-translate-y-0.5 transition-all duration-300">
                  <div className="bg-orange-100 p-3 rounded-lg group-hover/item:bg-orange-200 transition-colors">
                    <FaClock className="text-orange-600 text-base" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Office Hours</h4>
                    <p className="text-gray-600 text-sm">Mon-Sat: 9:00 AM – 6:00 PM<br />Sunday: Closed</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/50 backdrop-blur-sm p-8 md:p-10">
                <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FaPaperPlane className="text-blue-600" />
                  Send a Message
                </h3>
                <p className="text-gray-500 text-sm mb-5">Fill out the form and we'll get back to you soon.</p>

                {feedback.message && (
                  <div
                    className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
                      feedback.type === "success"
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : "bg-red-50 text-red-700 border border-red-200"
                    }`}
                  >
                    {feedback.type === "success" ? (
                      <FaCheckCircle className="flex-shrink-0" />
                    ) : (
                      <FaExclamationCircle className="flex-shrink-0" />
                    )}
                    <span>{feedback.message}</span>
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1.5 flex items-center gap-2">
                      <FaUser className="text-blue-500" /> Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50 focus:bg-white text-base"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1.5 flex items-center gap-2">
                      <FaEnvelope className="text-indigo-500" /> Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all bg-gray-50 focus:bg-white text-base"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1.5 flex items-center gap-2">
                      <FaPhoneAlt className="text-purple-500" /> Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 12345 67890"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all bg-gray-50 focus:bg-white text-base"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1.5 flex items-center gap-2">
                      <FaComment className="text-blue-500" /> Message *
                    </label>
                    <textarea
                      rows="4"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Write your message here..."
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50 focus:bg-white resize-none text-base"
                      required
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-lg font-medium text-base hover:shadow-md transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 group/btn disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Send Message</span>
                        <FaPaperPlane className="group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
                      </>
                    )}
                  </button>
                </form>
                <div className="mt-5 text-center text-sm text-gray-400 flex items-center justify-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  We'll respond within 24 hours
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12">
            <div className="text-center mb-5 animate-fade-in">
              <h3 className="text-2xl font-bold text-gray-800 mb-1 flex items-center justify-center gap-2">
                <FaMapMarkerAlt className="text-blue-600" />
                Find Us on Map
              </h3>
              <p className="text-gray-500 text-sm">Village - Kushnow, Post - Bhaupur, Jaunpur</p>
            </div>
            <div className="w-full rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-500">
              <iframe
                title="RDS College Location"
                src={mapEmbedUrl}
                width="100%"
                height="350"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                className="w-full"
              ></iframe>
            </div>
            <div className="text-center mt-4">
              <a
                href="https://maps.google.com/?q=Kushnow+Bhaupur+Jaunpur+Uttar+Pradesh"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-600 font-medium hover:gap-3 transition-all"
              >
                Get Directions on Google Maps <FaChevronRight />
              </a>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(2rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fade-up 0.6s ease-out forwards;
        }
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </section>
  );
};

export default ContactSection;